import { useState } from "react";
import { Search, Loader2, FileText, Calendar, Building, Clock, CheckCircle2, User, Send, Info, XCircle } from "lucide-react";
import InputText from "../../../components/ui/InputText";
import Button from "../../../components/ui/Button";
import Api from "../../../services/Api";
import { toast, Toaster } from "react-hot-toast";
import dayjs from "dayjs";

const STATUS_STEPS = [
    { key: "dikirim", label: "Dikirim", icon: Send },
    { key: "pending", label: "Pending", icon: Clock },
    { key: "ditolak", label: "Ditolak", icon: XCircle },
    { key: "proses", label: "Diproses", icon: FileText },
    { key: "menunggu", label: "Menunggu", icon: Loader2 },
    { key: "selesai", label: "Selesai", icon: CheckCircle2 },
];

export default function DataTracking() {
    const [kodeSurat, setKodeSurat] = useState("");
    const [loading, setLoading] = useState(false);
    const [trackingData, setTrackingData] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!kodeSurat.trim()) {
            toast.error("Mohon masukkan kode surat");
            return;
        }

        setLoading(true);
        setTrackingData(null);
        try {
            // Assuming endpoint structure, adjust as needed
            const res = await Api.get(`/surat-masuk/${kodeSurat}`);

            if (res.data) {
                setTrackingData(res.data);
            } else {
                toast.error("Data surat tidak ditemukan");
            }
        } catch (error) {
            console.error("Tracking error:", error);
            if (error.response?.status === 404) {
                toast.error("Kode surat tidak ditemukan");
            } else {
                toast.error("Gagal melacak surat. Silakan coba lagi.");
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusIndex = (status) => {
        return STATUS_STEPS.findIndex(s => s.key === status?.toLowerCase()) || 0;
    };

    const currentStatusIndex = trackingData ? getStatusIndex(trackingData.status_terakhir || trackingData.status) : -1;

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-6">
            <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                    Cek Status Surat
                </h1>
                <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                    Masukkan kode unik surat Anda untuk melacak posisi dan status terkini dokumen Anda secara real-time.
                </p>
            </div>

            {/* Search Section */}
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 border border-gray-100">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="w-full md:flex-1">
                        <InputText
                            id="kode_surat"
                            label="Kode Surat"
                            placeholder="Contoh: SKT-xxxx-xxxx"
                            value={kodeSurat}
                            onChange={(e) => setKodeSurat(e.target.value)}
                            className="bg-gray-50/50"
                        />
                    </div>
                    <div className="w-full md:w-auto pb-[1px]">
                        <Button
                            type="submit"
                            isLoading={loading}
                            disabled={loading || !kodeSurat}
                            className="w-full h-[46px]" // Match height of InputText usually around 42-46px depending on padding
                        >
                            {!loading && <Search className="w-4 h-4 mr-2" />}
                            Lacak Surat
                        </Button>
                    </div>
                </form>
            </div>

            {/* Result Section */}
            {trackingData && (
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="bg-primary/5 p-6 border-b border-gray-100">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {trackingData.perihal || "Detail Surat"}
                                </h2>
                                <p className="text-gray-500 mt-1 flex items-center text-sm">
                                    <span className="font-mono bg-white px-2 py-0.5 rounded border border-gray-200 mr-2">
                                        {trackingData.kode_surat || trackingData.kode || kodeSurat}
                                    </span>
                                </p>
                            </div>
                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-100 shadow-sm">
                                <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-sm font-medium text-gray-700 capitalize">
                                    {trackingData.status_terakhir || "Proses"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8">
                        {/* Timeline / Progress Bar */}
                        <div className="relative mb-12 mt-4">
                            {/* Connecting Line */}
                            <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-1/2 hidden md:block rounded-full" />

                            <div className="flex flex-col md:flex-row justify-between gap-8 md:gap-0 relative">
                                {STATUS_STEPS.map((step, index) => {
                                    const Icon = step.icon;
                                    const isCompleted = index <= currentStatusIndex;
                                    const isCurrent = index === currentStatusIndex;

                                    return (
                                        <div key={step.key} className="flex md:flex-col items-center gap-4 md:gap-2 z-10">
                                            <div className={`
                                                relative w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300
                                                ${isCompleted ? 'bg-primary text-white shadow-lg shadow-primary/30 scale-110' : 'bg-white border-2 border-gray-200 text-gray-300'}
                                                ${isCurrent ? 'ring-4 ring-primary/20' : ''}
                                            `}>
                                                <Icon className="w-5 h-5 md:w-6 md:h-6" />

                                                {/* Mobile Line Connector */}
                                                {index !== STATUS_STEPS.length - 1 && (
                                                    <div className={`absolute top-full left-1/2 w-0.5 h-8 -ml-[1px] md:hidden ${index < currentStatusIndex ? 'bg-primary' : 'bg-gray-200'}`} />
                                                )}
                                            </div>
                                            <div className="flex flex-col md:items-center">
                                                <span className={`text-sm md:text-base font-semibold transition-colors duration-300 ${isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                                                    {step.label}
                                                </span>
                                                {isCurrent && (
                                                    <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full mt-1">
                                                        Saat Ini
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Detailed Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-xl p-6 border border-gray-100">
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Nomor Surat</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">{trackingData.nosurat || "-"}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Asal Instansi</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">{trackingData.asal_instansi || "-"}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Tanggal Kegiatan</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {trackingData.tgl_kegiatan
                                                ? dayjs(trackingData.tgl_kegiatan).format("DD MMM YYYY")
                                                : "-"}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Nomor HP</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">{trackingData.hp || "-"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {trackingData.ket && (
                            <div className="mt-6 bg-blue-50/50 rounded-xl p-4 md:p-6 border border-blue-100/50">
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-blue-100/50 rounded-lg shrink-0">
                                        <Info className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-semibold text-blue-900 mb-1">Keterangan</h3>
                                        <p className="text-sm text-blue-700 leading-relaxed">
                                            {trackingData.ket}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Toaster />
        </div>
    );
}